import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── DB settings ───────────────────────────────────────────────────────────────

export function useAllSettings() {
  return useQuery<Record<string, string>>({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string>>('/api/settings'),
  });
}

export function useUpdateSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<{ ok: boolean }>(`/api/settings/key/${key}`, { value }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

// ── Keytar secrets ────────────────────────────────────────────────────────────

/** Claves PROPIAS de la cuenta. Ruta distinta de /secrets (que ahora es solo
 *  de plataforma) — un cliente nunca toca las claves de EvoHub. */
export function useOwnKeys(enabled = true) {
  return useQuery<{ keys: Array<{ key: string; configured: boolean }> }>({
    queryKey: ['own-keys'],
    queryFn: () => api.get<{ keys: Array<{ key: string; configured: boolean }> }>('/api/settings/own-keys'),
    enabled,
  });
}

export function useUpdateOwnKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<{ ok: boolean }>(`/api/settings/own-keys/${key}`, { value }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['own-keys'] }),
  });
}

export function useSecretStatus(key: string) {
  return useQuery<{ key: string; configured: boolean }>({
    queryKey: ['secret-status', key],
    queryFn: () => api.get<{ key: string; configured: boolean }>(`/api/settings/secrets/${key}`),
    // Solo platform admin: para una cuenta cliente la ruta da 403 y no tiene
    // sentido reintentar ni mostrar error — la sección ni se le renderiza.
    retry: false,
  });
}

export function useUpdateSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<{ ok: boolean }>(`/api/settings/secrets/${key}`, { value }),
    onSuccess: (_d, vars) => void qc.invalidateQueries({ queryKey: ['secret-status', vars.key] }),
  });
}

// ── LLM spend ─────────────────────────────────────────────────────────────────

export interface LlmSpend {
  todaySpend: number;
  cap: number;
  remaining: number;
  date: string;
}

export function useLlmSpend() {
  return useQuery<LlmSpend>({
    queryKey: ['llm-spend'],
    queryFn: () => api.get<LlmSpend>('/api/llm/spend'),
    refetchInterval: 60_000,
  });
}

// ── IMAP accounts ─────────────────────────────────────────────────────────────

export function useEmailAccounts() {
  return useQuery<string[]>({
    queryKey: ['email-accounts'],
    queryFn: () => api.get<string[]>('/api/email/accounts'),
  });
}

export function useAddEmailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; host: string; port?: string; secure?: string; user: string; pass: string }) =>
      api.post<{ ok: boolean; name: string }>('/api/email/accounts', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-accounts'] }),
  });
}

/** Motivo del fallo en clave neutra (REGLA #6) — el texto lo arma la UI con
 *  i18n. `detail` es el mensaje crudo del servidor, para cuando el motivo es
 *  'unknown' y no hay nada mejor que mostrar. */
export type ImapTestReason = 'auth' | 'host' | 'tls' | 'timeout' | 'no_credentials' | 'unknown';
export interface ImapTestResult { ok: boolean; mailboxes?: string[]; reason?: ImapTestReason; detail?: string }

export function useTestEmailAccount() {
  return useMutation({
    // encodeURIComponent porque los nombres de casilla llevan espacios
    // ("Villa Sion") y hasta acento — sin esto la URL sale mal formada.
    mutationFn: (name: string) =>
      api.post<ImapTestResult>(`/api/email/accounts/${encodeURIComponent(name)}/test`, {}),
  });
}

// ── Account signature ─────────────────────────────────────────────────────────

export function useAccountSignature(name: string | undefined) {
  return useQuery<{ signature: string | null }>({
    queryKey: ['account-signature', name],
    queryFn: () => api.get<{ signature: string | null }>(`/api/email/accounts/${encodeURIComponent(name!)}/signature`),
    enabled: !!name,
    staleTime: 30_000,
  });
}

export function useUpdateAccountSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, signature }: { name: string; signature: string }) =>
      api.put<{ ok: boolean }>(`/api/email/accounts/${encodeURIComponent(name)}/signature`, { signature }),
    onSuccess: (_d, vars) => void qc.invalidateQueries({ queryKey: ['account-signature', vars.name] }),
  });
}

// ── Audit log ─────────────────────────────────────────────────────────────────

export interface AuditRow {
  id: number;
  action: string;
  entityType: string | null;
  entityId: string | null;
  paramsJson: string | null;
  createdAt: string;
}

export interface AuditResult {
  rows: AuditRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export function useAuditLog(page = 1, limit = 50) {
  return useQuery<AuditResult>({
    queryKey: ['audit-log', page, limit],
    queryFn: () =>
      api.get<AuditResult>(`/api/settings/audit-log?page=${page}&limit=${limit}`),
  });
}

// ── Lead Engine settings ───────────────────────────────────────────────────────

export interface LeadEngineSettings {
  followupCount: number;
  followupDelay1: number;
  followupDelay2: number;
  followupDelay3: number;
  signatureHtml: string;
  postalAddress: string;
  fromAccount: string;
  dailyCap: number;
  autoSendStartHour: number;
  autoSendEndHour: number;
  autoCloseGraceDays: number;
}

const DEFAULT_LEAD_ENGINE: LeadEngineSettings = {
  followupCount: 2,
  followupDelay1: 3,
  followupDelay2: 7,
  followupDelay3: 14,
  signatureHtml: '',
  postalAddress: '',
  fromAccount: '',
  dailyCap: 10,
  autoSendStartHour: 9,
  autoSendEndHour: 17,
  autoCloseGraceDays: 2,
};

export function useLeadEngineSettings() {
  return useQuery<LeadEngineSettings>({
    queryKey: ['lead-engine-settings'],
    queryFn: () => api.get<LeadEngineSettings>('/api/settings/lead-engine'),
    placeholderData: DEFAULT_LEAD_ENGINE,
  });
}

export function useUpdateLeadEngineSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<LeadEngineSettings>) =>
      api.patch<{ ok: boolean }>('/api/settings/lead-engine', patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-engine-settings'] }),
  });
}

// ── Lead pipelines ────────────────────────────────────────────────────────────

export type ScrapeZone = { city: string; state: string };

export interface LeadPipeline {
  id: number;
  name: string;
  enabled: number;
  categories: string[];
  zones: ScrapeZone[];
  capPerRun: number;
  cronHour: number;
  cronMinute: number;
  intent: string;
  autoScrape: number;
  autoEnrich: number;
  autoAnalysis: number;
  autoDraft: number;
  autoSend: number;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunCount: number | null;
  exhausted: boolean;
}

export interface LeadPipelineInput {
  name: string;
  enabled: boolean;
  categories: string[];
  zones: ScrapeZone[];
  capPerRun: number;
  cronHour: number;
  cronMinute: number;
  intent: string;
  autoScrape: boolean;
  autoEnrich: boolean;
  autoAnalysis: boolean;
  autoDraft: boolean;
  autoSend: boolean;
}

export function useLeadPipelines() {
  return useQuery<LeadPipeline[]>({
    queryKey: ['lead-pipelines'],
    queryFn: () => api.get<LeadPipeline[]>('/api/lead-pipelines'),
    placeholderData: [],
    refetchInterval: (query) => {
      const pipelines = query.state.data;
      return Array.isArray(pipelines) &&
        pipelines.some(p => p.lastRunStatus === 'running' || p.lastRunStatus === 'queued')
        ? 5_000
        : false;
    },
  });
}

export function useCreateLeadPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeadPipelineInput) =>
      api.post<{ ok: boolean; id: number }>('/api/lead-pipelines', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

export function useUpdateLeadPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & LeadPipelineInput) =>
      api.put<{ ok: boolean }>(`/api/lead-pipelines/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

export function useDeleteLeadPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ ok: boolean }>(`/api/lead-pipelines/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

export function useRunPipelineNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ ok: boolean; runsQueued: number }>(`/api/lead-pipelines/${id}/run-now`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead-pipelines'] }),
  });
}

// ── OpenClaw (Settings → OpenClaw) ───────────────────────────────────────────

export interface OpenClawStatus {
  consumer: string;
  endpoint: string;
  tokenIssued: boolean;
  lastSeenAt: string | null;
}

export function useOpenClawStatus() {
  return useQuery<OpenClawStatus>({
    queryKey: ['openclaw-status'],
    queryFn: () => api.get('/api/settings/openclaw'),
    staleTime: 10_000,
  });
}

export function useIssueOpenClawToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ token: string }>('/api/settings/openclaw/issue-token', {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['openclaw-status'] }),
  });
}

// ── Users (Fase 2 — Settings → Usuarios) ─────────────────────────────────────

export interface AppUser {
  id: number;
  email: string;
  displayName: string | null;
  role: 'owner' | 'member';
  active: boolean;
  createdAt: string;
}

export function useUsers() {
  return useQuery<{ users: AppUser[] }>({
    queryKey: ['users'],
    queryFn: () => api.get('/api/settings/users'),
    staleTime: 10_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; password: string; displayName?: string | undefined; role: 'owner' | 'member' }) =>
      api.post<AppUser>('/api/settings/users', data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: 'owner' | 'member' }) =>
      api.patch<AppUser>(`/api/settings/users/${id}/role`, { role }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.patch<AppUser>(`/api/settings/users/${id}/active`, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ── API usage (Fase 4 — Settings → Consumo) ──────────────────────────────────

export interface ApiUsageServiceBreakdown {
  service: string;
  quantity: number;
  costUsd: number;
}

export interface ApiUsageSummary {
  month: string;
  monthSpendUsd: number;
  estimatedChargeUsd: number;
  byService: ApiUsageServiceBreakdown[];
  limitUsd: number | null;
  billingExempt: boolean;
}

export function useApiUsage() {
  return useQuery<ApiUsageSummary>({
    queryKey: ['api-usage'],
    queryFn: () => api.get('/api/settings/api-usage'),
    staleTime: 30_000,
  });
}

export function useSetApiUsageLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (limitUsd: number | null) =>
      api.put<{ ok: boolean; limitUsd: number | null }>('/api/settings/api-usage/limit', { limitUsd }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['api-usage'] }),
  });
}

// ── Subscription (Fase 3 — Settings → Suscripción) ───────────────────────────

export interface SubscriptionSummary {
  billingExempt: boolean;
  status: 'active' | 'grace_period' | 'suspended';
  priceId: string | null;
  priceAmount: number | null;
  priceCurrency: string | null;
  priceInterval: string | null;
  hasCustomer: boolean;
  gracePeriodStartedAt: string | null;
  gracePeriodEndsAt: string | null;
  nextChargeAt: string | null;
  nextChargeAmount: number | null;
  nextChargeCurrency: string | null;
  paymentMethod: { brand: string; last4: string } | null;
  usage: {
    grossUsd: number; coveredUsd: number; billableUsd: number; multiplier: number;
    estimatedChargeCents: number; capCents: number | null; billingActive: boolean;
    byService: Array<{ label: string; grossQuantity: number; freeQuantity: number; billableCostUsd: number; tierQuantity: number | null }>;
  } | null;
  invoices: Array<{ id: string; date: string | null; amountPaid: number; currency: string; status: string | null; hostedInvoiceUrl: string | null }>;
  /** Nombre del plan del catálogo que corresponde a lo que paga esta cuenta.
   *  null = precio de excepción, o plan dado de baja. */
  planName: string | null;
  /** Período de prueba (2026-08-18). null = esta cuenta nunca tuvo prueba.
   *  Llega también vencida: quien perdió el acceso tiene que ver por qué. */
  trial: { endsAt: string; daysLeft: number; active: boolean } | null;
}

export function useSubscription() {
  return useQuery<SubscriptionSummary>({
    queryKey: ['subscription'],
    queryFn: () => api.get('/api/settings/subscription'),
    staleTime: 10_000,
  });
}

// useSetSubscriptionPrice ELIMINADO (2026-08-18): el precio de una cuenta lo
// decide EvoHub desde el panel de plataforma, no el cliente desde sus propios
// Ajustes. La ruta del backend ya no existe. El cliente lo sigue VIENDO en
// useSubscription().priceId.

/** Planes del catálogo que puede contratar esta cuenta — lo que ve el cliente
 *  en el paso 7. El monto lo resuelve el backend contra Stripe, así que puede
 *  venir null: la UI muestra "—", nunca un número inventado. */
export interface AvailablePlan {
  id: string;
  name: string;
  description: string | null;
  amount: number | null;
  currency: string | null;
  interval: string | null;
}

export function useAvailablePlans() {
  return useQuery<{ plans: AvailablePlan[] }>({
    queryKey: ['subscription', 'plans'],
    queryFn: () => api.get('/api/settings/subscription/plans'),
    staleTime: 30_000,
  });
}

export function useCreateSubscriptionCheckout() {
  return useMutation({
    // planId = el plan que eligió el cliente. Si la cuenta tiene un precio de
    // excepción, el backend lo ignora y usa la excepción.
    mutationFn: (planId?: string) =>
      api.post<{ url: string }>('/api/settings/subscription/checkout', planId ? { planId } : {}),
  });
}

export function useCreateBillingPortal() {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/settings/subscription/portal', {}),
  });
}
